/**
 * OAuth callback page - handles Scalekit OAuth callback
 * Backend handles token exchange and sets cookie, then redirects here
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'react-toastify';

export const Callback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { initialize } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processCallback = async () => {
      try {
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (errorParam) {
          setError(errorDescription || errorParam);
          setIsProcessing(false);
          toast.error(errorDescription || 'Authentication failed');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        await initialize();
        toast.success('Successfully signed in!');
        navigate('/dashboard');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to complete authentication';
        setError(message);
        setIsProcessing(false);
        toast.error(message);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processCallback();
  }, [searchParams, navigate, initialize]);

  return (
    <div className="mx-auto flex min-h-screen max-w-sm flex-col items-center justify-center p-4">
      <Card className="w-full">
        <CardContent className="pt-6">
          {isProcessing ? (
            <div className="py-8 text-center">
              <Loader2 className="mx-auto mb-4 size-8 animate-spin text-muted-foreground" />
              <p className="text-muted-foreground">Completing authentication...</p>
            </div>
          ) : error ? (
            <div>
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <p className="text-center text-sm text-muted-foreground">
                Redirecting to login page...
              </p>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
